import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, ShieldAlert, User as UserIcon, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/Modal';
import { NavIcon, type NavIconName } from '@/components/icons/NavIcons';
import { FundWalletModal } from '@/components/user/FundWalletModal';
import { PinSetupModal } from '@/components/user/PinSetupModal';
import { ROUTES } from '@/constants/routes';
import { mobileNav, userBillsNav, userBottomNav, userMainNav, userServicesNav } from '@/constants/userNavigation';
import { useUserAuth } from '@/context/UserAuthContext';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { useWallet } from '@/hooks/useWallet';
import { cn } from '@/utils/cn';
import { BrandLogo } from './LandingNavbar';
import { SidebarStoreBadges } from '@/components/shared/StoreBadges';

import type { WalletSummary } from '@/types/wallet';

interface DashboardUiContextValue {
  openFundModal: () => void;
  openPinModal: () => void;
  wallet: WalletSummary | null;
  walletLoading: boolean;
  generatingVa: boolean;
  loadWallet: () => Promise<WalletSummary | null>;
  generateVirtualAccount: () => Promise<WalletSummary['virtualAccount'] | null>;
  hasVirtualAccount: boolean;
  fundingEnabled: boolean;
}

const DashboardUiContext = createContext<DashboardUiContextValue | null>(null);

export function useDashboardUi() {
  const ctx = useContext(DashboardUiContext);
  if (!ctx) throw new Error('useDashboardUi must be used within UserLayout');
  return ctx;
}

export function UserLayout() {
  const navigate = useNavigate();
  const { user, logout, persistUser, dismissPinSetup, pinSetupDismissed } = useUserAuth();
  const { loading: loggingOut, run } = useAsyncAction();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [fundModalOpen, setFundModalOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const walletState = useWallet();
  const needsPinSetup = Boolean(user && !user.pinEnabled);
  const shouldAutoShowPinModal = needsPinSetup && !pinSetupDismissed;

  useEffect(() => {
    if (shouldAutoShowPinModal) {
      setPinModalOpen(true);
    }
  }, [shouldAutoShowPinModal]);

  function handlePinLater() {
    dismissPinSetup();
    setPinModalOpen(false);
  }

  async function confirmLogout() {
    await run(async () => {
      await logout();
      navigate(ROUTES.login);
    });
  }

  return (
    <DashboardUiContext.Provider
      value={{
        openFundModal: () => setFundModalOpen(true),
        openPinModal: () => setPinModalOpen(true),
        wallet: walletState.wallet,
        walletLoading: walletState.loading,
        generatingVa: walletState.generating,
        loadWallet: walletState.loadWallet,
        generateVirtualAccount: walletState.generateVirtualAccount,
        hasVirtualAccount: walletState.hasVirtualAccount,
        fundingEnabled: walletState.fundingEnabled,
      }}
    >
      <div className="min-h-screen bg-brand-surface/40 pb-[4.75rem] lg:pb-0">
        <UserSidebar
          onLogoutRequest={() => setShowLogoutConfirm(true)}
          loggingOut={loggingOut}
          showPinReminder={needsPinSetup}
          onOpenPinSetup={() => setPinModalOpen(true)}
        />
        <div className="lg:pl-64">
          <UserNavbar
            onLogoutRequest={() => setShowLogoutConfirm(true)}
            showPinReminder={needsPinSetup}
            onOpenPinSetup={() => setPinModalOpen(true)}
          />
          {needsPinSetup && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 lg:hidden">
              <button
                type="button"
                onClick={() => setPinModalOpen(true)}
                className="flex w-full items-center gap-2.5 text-left"
              >
                <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600" />
                <span className="min-w-0 flex-1 text-xs text-amber-900">
                  <span className="font-semibold">Set transaction PIN</span>
                  <span className="text-amber-800"> — required for wallet payments</span>
                </span>
                <span className="shrink-0 rounded-full bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white">
                  Set PIN
                </span>
              </button>
            </div>
          )}
          <main className="mx-auto max-w-6xl p-4 pb-32 sm:p-6 sm:pb-36">
            <Outlet />
          </main>
        </div>

        <MobileBottomNav />

        <ConfirmDialog
          open={showLogoutConfirm}
          title="Logout?"
          message="Are you sure you want to sign out of your account?"
          confirmLabel="Logout"
          variant="danger"
          loading={loggingOut}
          onConfirm={() => void confirmLogout()}
          onCancel={() => setShowLogoutConfirm(false)}
        />

        <FundWalletModal
          open={fundModalOpen}
          onClose={() => setFundModalOpen(false)}
          account={walletState.wallet?.virtualAccount ?? null}
          chargePercent={walletState.wallet?.chargePercent ?? 0}
          chargeCap={walletState.wallet?.chargeCap ?? 0}
          fundingEnabled={walletState.fundingEnabled}
          generating={walletState.generating}
          onGenerate={() => void walletState.generateVirtualAccount()}
        />

        <PinSetupModal
          open={pinModalOpen}
          onClose={() => setPinModalOpen(false)}
          onSetLater={handlePinLater}
          onSuccess={(nextUser) => {
            persistUser(nextUser);
            setPinModalOpen(false);
          }}
        />
      </div>
    </DashboardUiContext.Provider>
  );
}

function UserSidebar({
  onLogoutRequest,
  loggingOut,
  showPinReminder,
  onOpenPinSetup,
}: {
  onLogoutRequest: () => void;
  loggingOut: boolean;
  showPinReminder: boolean;
  onOpenPinSetup: () => void;
}) {
  const location = useLocation();
  const { user } = useUserAuth();
  const servicesActive =
    location.pathname === userServicesNav.to || location.pathname.startsWith('/dashboard/verify');
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  return (
    <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col bg-brand-navy lg:flex">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-brand" />
      <div className="flex h-16 shrink-0 items-center border-b border-white/10 px-5">
        <BrandLogo />
      </div>

      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {userMainNav.map((item) => (
          <SidebarLink key={item.to} to={item.to} end={item.end} icon={item.icon}>
            {item.label}
          </SidebarLink>
        ))}

        {userBillsNav.map((item) => (
          <SidebarLink key={item.to} to={item.to} icon={item.icon}>
            {item.label}
          </SidebarLink>
        ))}

        <NavLink
          to={userServicesNav.to}
          className={() =>
            cn(
              'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
              servicesActive
                ? 'bg-white/10 text-secondary'
                : 'text-slate-400 hover:bg-white/5 hover:text-white',
            )
          }
        >
          <NavIcon name={userServicesNav.icon!} className={servicesActive ? 'text-secondary' : undefined} />
          {userServicesNav.label}
        </NavLink>

        {userBottomNav.map((item) => (
          <SidebarLink key={item.to} to={item.to} icon={item.icon}>
            {item.label}
          </SidebarLink>
        ))}
      </nav>

      {showPinReminder && (
        <div className="shrink-0 border-t border-amber-400/20 bg-amber-500/10 px-3 py-2.5">
          <button
            type="button"
            onClick={onOpenPinSetup}
            className="w-full rounded-xl border border-amber-400/40 bg-amber-500/15 p-3 text-left transition hover:bg-amber-500/25"
          >
            <div className="flex items-start gap-2.5">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
              <div>
                <p className="text-xs font-semibold text-amber-50">Set transaction PIN</p>
                <p className="mt-0.5 text-[11px] leading-snug text-amber-100/90">
                  Required for wallet payments. Tap to set up now.
                </p>
              </div>
            </div>
          </button>
        </div>
      )}

      <div className="shrink-0 border-t border-white/10 px-3 pt-3">
        <SidebarStoreBadges />
      </div>

      <div className="shrink-0 border-t border-white/10 p-3">
        <div className="rounded-xl bg-white/5 p-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-brand text-xs font-bold text-white">
              {initials}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-white">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="truncate text-xs capitalize text-slate-400">{user?.userType}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            fullWidth
            className="mt-3 border-white/20 bg-transparent text-white hover:bg-white/10"
            loading={loggingOut}
            loadingText="Logging out..."
            onClick={onLogoutRequest}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </div>
    </aside>
  );
}

function SidebarLink({
  to,
  end,
  nested,
  icon,
  children,
}: {
  to: string;
  end?: boolean;
  nested?: boolean;
  icon?: NavIconName;
  children: ReactNode;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
          nested && 'py-2 text-[13px]',
          isActive ? 'bg-white/10 text-secondary' : 'text-slate-400 hover:bg-white/5 hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          {icon && <NavIcon name={icon} className={isActive ? 'text-secondary' : undefined} />}
          {children}
        </>
      )}
    </NavLink>
  );
}

function UserNavbar({
  onLogoutRequest,
  showPinReminder,
  onOpenPinSetup,
}: {
  onLogoutRequest: () => void;
  showPinReminder: boolean;
  onOpenPinSetup: () => void;
}) {
  const { user } = useUserAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-gray-200 bg-white/95 px-4 backdrop-blur sm:h-16 sm:px-6">
      <div className="flex min-w-0 items-center gap-3 lg:flex-1">
        <div className="lg:hidden">
          <BrandLogo subtitle={user?.userType} />
        </div>
        <p className="hidden truncate font-semibold text-brand-navy lg:block">Welcome back, {user?.firstName}</p>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {showPinReminder && (
          <button
            type="button"
            onClick={onOpenPinSetup}
            className="inline-flex items-center gap-1.5 rounded-full bg-red-500 px-2.5 py-1.5 text-xs font-semibold text-white transition hover:bg-red-600 sm:px-3"
          >
            <ShieldAlert className="h-3.5 w-3.5 sm:hidden" />
            <span className="hidden sm:inline">Set PIN</span>
            <span className="sm:hidden">PIN</span>
          </button>
        )}
        <div className="flex items-center gap-1.5 rounded-full bg-brand-surface px-3 py-1.5 text-xs sm:rounded-xl sm:px-4 sm:py-2 sm:text-sm">
          <Wallet className="h-4 w-4 text-secondary" strokeWidth={2} />
          <span className="font-bold text-brand-navy">₦{user?.walletBalance?.toLocaleString() ?? '0'}</span>
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white p-1 pr-3 transition hover:border-primary/30"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-brand text-sm font-bold text-white">
              {initials}
            </span>
            <span className="hidden text-sm font-medium text-brand-navy sm:inline">{user?.firstName}</span>
            <ChevronDown className="h-4 w-4 text-brand-muted" />
          </button>

          {open && (
            <div className="absolute right-0 top-full z-50 mt-2 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white py-1 shadow-card">
              <NavLink
                to={ROUTES.dashboardProfile}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm text-brand-navy hover:bg-brand-surface"
              >
                <UserIcon className="h-4 w-4 text-brand-muted" strokeWidth={2} />
                Profile
              </NavLink>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onLogoutRequest();
                }}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"
              >
                <LogOut className="h-4 w-4" strokeWidth={2} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function MobileBottomNav() {
  const location = useLocation();

  function isNavActive(item: (typeof mobileNav)[number]) {
    if (item.matchPaths?.some((path) => location.pathname.startsWith(path))) return true;
    if (item.end) return location.pathname === item.to;
    return location.pathname.startsWith(item.to);
  }

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-brand-navy pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_24px_rgba(0,0,0,0.15)] lg:hidden">
      <div className="flex">
        {mobileNav.map((item) => {
          const active = isNavActive(item);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className="flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5"
            >
              <span
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-xl transition-colors',
                  active ? 'bg-secondary/15 text-secondary' : 'text-slate-400',
                )}
              >
                {item.icon && <NavIcon name={item.icon} className="h-5 w-5" />}
              </span>
              <span className={cn('text-[10px] font-medium', active ? 'text-secondary' : 'text-slate-400')}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
